import { useEffect, useRef } from 'react'
import { useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { fetchNotifications, markNotificationRead } from '../api'
import { getNotificationSemantic } from '../lib/notification-semantic'
import { resolveNotificationActionUrl } from '../lib/notification-router'
import { getLocalizedNotification } from '../lib/notification-content'
import { useAuthSession } from '../../auth/session'

const SHOWN_NOTIFICATIONS_KEY = 'notification-toasts-shown'

/**
 * Map notification event types to the TanStack Query keys they should invalidate.
 * This lets the UI auto-refresh relevant tables when a notification toast arrives.
 */
const notificationEventToQueryKeys: Record<string, QueryKey[]> = {
  exchange_requested: [['exchange-requests'], ['notifications'], ['points']],
  exchange_approved: [['exchange-requests'], ['notifications'], ['points'], ['transactions']],
  exchange_rejected: [['exchange-requests'], ['notifications'], ['points']],
  exchange_processing: [['exchange-requests'], ['notifications']],
  exchange_completed: [['exchange-requests'], ['notifications'], ['points'], ['transactions']],
  exchange_cancelled: [['exchange-requests'], ['notifications'], ['points']],

  program_assigned: [['programs'], ['dashboard'], ['notifications']],
  program_unassigned: [['programs'], ['dashboard'], ['notifications']],
  program_activated: [['programs'], ['dashboard'], ['notifications']],
  program_paused: [['programs'], ['dashboard'], ['notifications']],
  program_resumed: [['programs'], ['dashboard'], ['notifications']],
  program_suspended: [['programs'], ['dashboard'], ['notifications']],
  program_rewards_updated: [['programs'], ['notifications']],
  program_archived: [['programs'], ['dashboard'], ['notifications']],

  agent_invited: [['agents'], ['dashboard'], ['notifications']],
  agent_activation: [['agents'], ['dashboard'], ['notifications']],
  agent_suspended: [['agents'], ['dashboard'], ['notifications']],
  agent_reactivated: [['agents'], ['dashboard'], ['notifications']],

  business_approved: [['businesses'], ['dashboard'], ['notifications']],
  business_rejected: [['businesses'], ['dashboard'], ['notifications']],

  prospect_submitted: [['prospects'], ['dashboard'], ['notifications']],
  prospect_submitted_public: [['prospects'], ['dashboard'], ['notifications']],
  prospect_status_changed: [['prospects'], ['dashboard'], ['notifications']],
  prospect_converted: [['prospects'], ['transactions'], ['points'], ['dashboard'], ['notifications']],
  prospect_lost: [['prospects'], ['dashboard'], ['notifications']],

  transaction_detected: [['transactions'], ['points'], ['prospects'], ['dashboard'], ['notifications']],
  transaction_validated: [['transactions'], ['points'], ['prospects'], ['dashboard'], ['notifications']],
  transaction_paid: [['transactions'], ['points'], ['prospects'], ['dashboard'], ['notifications']],

  points_earned: [['points'], ['transactions'], ['dashboard'], ['notifications']],

  sync_job_failed: [['sync'], ['notifications']],
  sync_job_dead_lettered: [['sync'], ['notifications']],
}

function getShownIds(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SHOWN_NOTIFICATIONS_KEY)
    if (raw) {
      return new Set(JSON.parse(raw))
    }
  } catch {
    // ignore
  }
  return new Set()
}

function saveShownIds(ids: Set<string>) {
  try {
    sessionStorage.setItem(SHOWN_NOTIFICATIONS_KEY, JSON.stringify(Array.from(ids)))
  } catch {
    // ignore
  }
}

/**
 * Watches the notification query and shows sonner toasts for new unread notifications.
 * Also invalidates relevant TanStack Query caches so tables auto-refresh.
 * Call this once at the app root level.
 */
export function useNotificationToasts() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const shownIdsRef = useRef<Set<string>>(getShownIds())
  const { isAuthenticated } = useAuthSession()

  const notificationsQuery = useQuery({
    queryKey: ['notifications', 'header'],
    queryFn: fetchNotifications,
    enabled: isAuthenticated,
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  })

  useEffect(() => {
    const records = notificationsQuery.data?.data ?? []
    const unreadRecords = records.filter((item) => !item.read_at)

    for (const item of unreadRecords) {
      if (shownIdsRef.current.has(item.id)) {
        continue
      }

      shownIdsRef.current.add(item.id)
      saveShownIds(shownIdsRef.current)

      const semantic = getNotificationSemantic(item)
      const actionUrl = resolveNotificationActionUrl(item)
      const { title, message } = getLocalizedNotification(item, t)
      const Icon = semantic.icon

      // ── Invalidate relevant data queries so tables refresh automatically ──
      const event = (item.metadata?.event as string) ?? ''
      const keysToInvalidate = notificationEventToQueryKeys[event]
      if (keysToInvalidate) {
        for (const queryKey of keysToInvalidate) {
          queryClient.invalidateQueries({ queryKey })
        }
      }

      toast.custom(
        (toastId) => (
          <div
            className="flex w-[340px] items-start gap-3 rounded-xl border bg-card p-3 shadow-lg cursor-pointer"
            onClick={() => {
              toast.dismiss(toastId)
              if (actionUrl) {
                window.location.href = actionUrl
              }
            }}
          >
            <div
              className={`mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full border ${semantic.mediaClass}`}
            >
              <Icon className={`size-4 ${semantic.iconClass}`} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-foreground leading-snug">
                {title}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2">
                {message}
              </p>
              <div className="mt-2 flex items-center gap-2">
                {actionUrl && (
                  <button
                    type="button"
                    className="text-xs font-medium text-primary hover:underline"
                    onClick={(e) => {
                      e.stopPropagation()
                      toast.dismiss(toastId)
                      window.location.href = actionUrl
                    }}
                  >
                    {t('notifications.page.view')}
                  </button>
                )}
                <button
                  type="button"
                  className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                  onClick={(e) => {
                    e.stopPropagation()
                    toast.dismiss(toastId)
                    markNotificationRead(item.id).then(() => {
                      queryClient.invalidateQueries({ queryKey: ['notifications'] })
                    })
                  }}
                >
                  {t('notifications.page.read')}
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                toast.dismiss(toastId)
              }}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <svg className="size-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ),
        {
          duration: 8000,
          position: 'bottom-right',
          closeButton: false,
        }
      )
    }
  }, [notificationsQuery.data, queryClient, t])
}
